/**
 * Сворачиваемая и растягиваемая боковая панель (паттерн VisualizerLayersPanel).
 */
import {
  useCallback,
  useState,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
} from "react";
import { ChevronsLeft, ChevronsRight, GripVertical } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { clampTacticalRailWidth } from "./tacticalSideRail";

type Side = "left" | "right";

type Props = {
  side: Side;
  title: string;
  collapsed: boolean;
  onCollapsedChange: (collapsed: boolean) => void;
  widthPx: number;
  onWidthChange: (widthPx: number) => void;
  headerExtra?: ReactNode;
  children: ReactNode;
  className?: string;
};

export function TacticalSideRail({
  side,
  title,
  collapsed,
  onCollapsedChange,
  widthPx,
  onWidthChange,
  headerExtra,
  children,
  className,
}: Props) {
  const [resizing, setResizing] = useState(false);
  const borderClass = side === "left" ? "border-r" : "border-l";

  const startResize = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      if (event.pointerType === "mouse" && event.button !== 0) return;
      event.preventDefault();
      const handle = event.currentTarget;
      const startX = event.clientX;
      const startW = widthPx;
      setResizing(true);
      try {
        handle.setPointerCapture(event.pointerId);
      } catch {
        /* ignore */
      }
      const onMove = (ev: PointerEvent) => {
        const delta = side === "left" ? ev.clientX - startX : startX - ev.clientX;
        onWidthChange(clampTacticalRailWidth(startW + delta));
      };
      const onUp = (ev: PointerEvent) => {
        try {
          if (handle.hasPointerCapture(ev.pointerId)) handle.releasePointerCapture(ev.pointerId);
        } catch {
          /* ignore */
        }
        setResizing(false);
        document.removeEventListener("pointermove", onMove);
        document.removeEventListener("pointerup", onUp);
        document.removeEventListener("pointercancel", onUp);
      };
      document.addEventListener("pointermove", onMove);
      document.addEventListener("pointerup", onUp);
      document.addEventListener("pointercancel", onUp);
    },
    [onWidthChange, side, widthPx],
  );

  if (collapsed) {
    const ExpandIcon = side === "left" ? ChevronsRight : ChevronsLeft;
    return (
      <aside
        className={cn(
          "flex h-full w-10 shrink-0 flex-col items-center gap-2 bg-card py-2",
          borderClass,
          className,
        )}
      >
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-8 w-8"
          onClick={() => onCollapsedChange(false)}
          aria-label={`Развернуть: ${title}`}
          title="Развернуть"
        >
          <ExpandIcon className="h-4 w-4" />
        </Button>
        <div
          className="mt-1 select-none text-xs font-medium uppercase tracking-wide text-muted-foreground"
          style={{ writingMode: "vertical-rl" }}
        >
          {title}
        </div>
      </aside>
    );
  }

  const CollapseIcon = side === "left" ? ChevronsLeft : ChevronsRight;
  const handleSideClass =
    side === "left"
      ? "right-0 translate-x-1.5"
      : "left-0 -translate-x-1.5";

  return (
    <aside
      className={cn(
        "relative flex h-full shrink-0 flex-col overflow-hidden bg-card",
        borderClass,
        !resizing && "transition-[width] duration-150 ease-out",
        className,
      )}
      style={{ width: widthPx }}
    >
      <div
        role="separator"
        aria-orientation="vertical"
        aria-label={`Изменить ширину: ${title}`}
        title="Потяните, чтобы изменить ширину"
        className={cn(
          "group absolute bottom-0 top-0 z-20 flex w-3 cursor-col-resize touch-none select-none items-center justify-center hover:bg-muted/40",
          handleSideClass,
        )}
        onPointerDown={startResize}
      >
        <span
          className={cn(
            "flex h-12 w-1 items-center justify-center rounded-full bg-border group-hover:bg-primary/50",
            resizing && "bg-primary",
          )}
        >
          <GripVertical className="h-3.5 w-3.5 text-muted-foreground" />
        </span>
      </div>

      <div className="flex items-center gap-1 border-b px-2 py-2">
        <h2 className="min-w-0 truncate text-sm font-semibold">{title}</h2>
        {headerExtra}
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="ml-auto h-8 w-8 shrink-0"
          onClick={() => onCollapsedChange(true)}
          aria-label={`Свернуть: ${title}`}
          title="Свернуть"
        >
          <CollapseIcon className="h-4 w-4" />
        </Button>
      </div>

      <div className="flex min-h-0 flex-1 flex-col overflow-hidden">{children}</div>
    </aside>
  );
}
